import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, CheckCircle2, ClipboardList, Award, ArrowRight, PlayCircle } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useApi } from "../../lib/api";
import { Stagger, Item, AnimatedCard, Counter, AnimatedProgress, EASE } from "../../components/motion";
import { ErrorState, SkeletonGrid, EmptyState, Badge } from "../../components/common";
import CourseCard from "../../components/CourseCard";

const STATS = [
  ["active", "Aktif Eğitimler", BookOpen, "text-[var(--brand)] bg-[var(--brand-50)]"],
  ["completed", "Tamamlanan Eğitimler", CheckCircle2, "text-emerald-600 bg-emerald-50"],
  ["pending_exams", "Bekleyen Sınavlar", ClipboardList, "text-amber-600 bg-amber-50"],
  ["certificates", "Sertifikalarım", Award, "text-indigo-600 bg-indigo-50"],
];

export const StatCard = ({ label, icon: Icon, tone, value, testid }) => (
  <AnimatedCard className="group p-5">
    <div className={`mb-5 grid h-11 w-11 place-items-center rounded-xl ${tone} transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-110`}>
      <Icon className="h-5 w-5" strokeWidth={1.8} />
    </div>
    <Counter value={value} className="block text-3xl font-extrabold tracking-tight text-[var(--ink)]" testid={testid} />
    <p className="mt-1 text-sm font-medium text-slate-500">{label}</p>
  </AnimatedCard>
);

function ContinueCard({ c }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3, ease: EASE }} className="surface gradient-border group relative overflow-hidden" data-testid="continue-card">
      <div className="grid md:grid-cols-[minmax(0,420px)_1fr]">
        <div className="relative aspect-[16/10] md:aspect-auto md:min-h-[260px] overflow-hidden bg-slate-100">
          {c.cover_url && <img src={c.cover_url} alt={c.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />}
          <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/50 to-transparent" />
          <PlayCircle className="absolute bottom-4 left-4 h-10 w-10 text-white/90 transition-transform duration-300 group-hover:scale-110" strokeWidth={1.4} />
        </div>
        <div className="relative p-6 sm:p-8">
          <div className="orb -right-20 -top-20 h-56 w-56" style={{ background: "rgba(227,10,23,0.12)" }} />
          <div className="relative">
            <div className="flex items-center gap-2">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--brand)]">Eğitime Devam Et</p>
              {c.is_demo && <Badge status="demo" />}
            </div>
            <h2 className="mt-2 text-2xl font-extrabold tracking-tight text-[var(--ink)]" data-testid="continue-course-title">{c.title}</h2>
            {c.last_lesson && <p className="mt-1.5 text-sm text-slate-500" data-testid="continue-last-lesson">{c.last_lesson}</p>}
            <div className="mt-7 flex items-end justify-between">
              <span className="text-sm font-semibold text-slate-500">İlerleme</span>
              <span className="text-lg font-extrabold text-[var(--ink)]" data-testid="continue-progress">%{c.progress} Tamamlandı</span>
            </div>
            <AnimatedProgress value={c.progress} height="h-2.5" className="mt-2" />
            <Link to={c.next_lesson_id ? `/panel/egitimlerim/${c.id}/ders/${c.next_lesson_id}` : `/panel/egitimlerim/${c.id}`} className="btn-primary mt-7 inline-flex h-12 w-full sm:w-auto items-center justify-center gap-2 rounded-xl px-6 text-sm font-semibold group/cta" data-testid="continue-cta-btn">
              Eğitime Devam Et <ArrowRight className="h-4 w-4 transition-transform group-hover/cta:translate-x-1" />
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { data, loading, error, reload } = useApi("/me/dashboard");
  return (
    <div>
      <Stagger gap={0.08} className="mb-8">
        <Item>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[var(--ink)]" data-testid="dashboard-greeting">
            Hoş Geldiniz, {user?.first_name} 👋
          </h1>
        </Item>
        <Item>
          <p className="mt-2 text-sm sm:text-base text-slate-500">Eğitim yolculuğunuza kaldığınız yerden devam edin.</p>
        </Item>
      </Stagger>
      {error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : loading ? (
        <div className="space-y-6"><SkeletonGrid count={4} className="h-36" cols="grid-cols-2 xl:grid-cols-4" /><div className="skeleton h-72" /></div>
      ) : (
        <div className="space-y-10">
          <Stagger className="grid grid-cols-2 gap-4 xl:grid-cols-4 sm:gap-5" gap={0.08}>
            {STATS.map(([k, label, icon, tone]) => (
              <StatCard key={k} label={label} icon={icon} tone={tone} value={data.stats[k]} testid={`stat-${k}`} />
            ))}
          </Stagger>
          {data.continue ? <ContinueCard c={data.continue} /> : <EmptyState icon={BookOpen} title="Henüz atanmış bir eğitiminiz bulunmuyor." desc="Eğitimleriniz atandığında burada görüntülenecektir." />}
          {data.courses.length > 0 && (
            <section>
              <div className="mb-5 flex items-center justify-between">
                <h2 className="text-lg font-bold text-[var(--ink)]">Eğitimlerim</h2>
                <Link to="/panel/egitimlerim" className="link-reveal text-sm font-semibold text-[var(--brand)]" data-testid="dashboard-all-courses-link">Tümünü Gör</Link>
              </div>
              <Stagger className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3" gap={0.08}>
                {data.courses.slice(0, 3).map((c) => <CourseCard key={c.id} c={c} />)}
              </Stagger>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
